import axios from "axios";
import { ACCESS_TOKEN_KEY } from "@services/api/constants";
import { EXCLUDED_401_PATHS } from "@services/route/exclude401Path";
import { getLanguageFromUrl } from "@utils/getLanguage";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5000/api";

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export const httpClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json"
  },
  withCredentials: true
});

// Instance séparée pour les calls de refresh (sans interceptors infinis)
const axiosRefresh = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json"
  },
  withCredentials: true
});

httpClient.interceptors.request.use((config) => {

  const currentLanguage = getLanguageFromUrl();
  config.headers["Accept-Language"] = currentLanguage;

  const tokenData = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (tokenData) {
    try {
      const parsedTokenData = JSON.parse(tokenData);
      if (parsedTokenData.token) {
        config.headers.Authorization = `Bearer ${parsedTokenData.token}`;
      }
    } catch (error) {
      // Invalid token data format, remove it
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const originalUrl = originalRequest?.url;

    const shouldSkip401 = EXCLUDED_401_PATHS.some((path) =>
      originalUrl?.includes(path)
    );
    
    // ✅ Ne pas déclencher refresh sur les endpoints de refresh eux-mêmes
    const isRefreshEndpoint =
      originalUrl?.includes("/auth/refresh-token") ||
      originalUrl?.includes("/offer-applicant/refresh-token");

    if (error.response?.status === 401  && !shouldSkip401 && !isRefreshEndpoint) {
      if (originalRequest._retry) {
        window.location.href = `/logout`;
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // ✅ Si refresh est déjà en cours, ajouter à la queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return httpClient(originalRequest);
        });
      }

      isRefreshing = true;

      try  {
        const tokenData = JSON.parse(localStorage.getItem(ACCESS_TOKEN_KEY) || "{}");
        const refreshEndpoint = tokenData.type === "applicant"
          ? "/offer-applicant/refresh-token"
          : "/auth/refresh-token";
        const { data } = await axiosRefresh.post(refreshEndpoint);
        const newAccessToken = data?.data?.token;
        
        // Calculer l'expiration (15 minutes par défaut, correspondant à JWT_EXPIRES_IN)
        const expiresAt = data?.data?.expiresAt; // 15 minutes
        
        // Sauvegarder le nouveau token avec sa date d'expiration
        const refreshedTokenData = {
          token: newAccessToken,
          expiresAt,
          type: tokenData.type || "recruiter"
        };
        localStorage.setItem(ACCESS_TOKEN_KEY, JSON.stringify(refreshedTokenData));
        
        httpClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return httpClient(originalRequest);
      } catch (err) {
        processQueue(err, null);
        const currentUrl = window.location.pathname + window.location.search;
        sessionStorage.setItem('redirect_target_url', currentUrl);
        window.location.href = `/logout`;
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
